import {catchError, map, of, switchMap, tap, withLatestFrom} from 'rxjs';

import {inject, Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Store} from '@ngrx/store';
import {SnackbarTypes, ToastService} from '@utils';

import {MetaTagsService} from '../services';
import {metaTagsActions} from './meta-tags.actions';
import selectMetaTagsState from './meta-tags.selectors';

@Injectable()
export class MetaTagsEffects {
	private actions$ = inject(Actions);
	private metaTagsService: MetaTagsService = inject(MetaTagsService);
	protected toastService: ToastService = inject(ToastService);
	private store = inject(Store);

	loadMetaTags$ = createEffect(() =>
		this.actions$.pipe(
			ofType(metaTagsActions.loadMetaTags),
			withLatestFrom(this.store.select(selectMetaTagsState)),
			switchMap(([action, state]) => {
				if (state.loaded) {
					return of(metaTagsActions.loadMetaTagsSuccess({metaTags: state.ids.map(id => state.entities[id])}));
				} else {
					return this.metaTagsService.getMetaTagKeys().pipe(
						map(metaTags => metaTags.filter(metaTag => action.containDisables || metaTag.isEnabled)),
						switchMap(response => of(metaTagsActions.loadMetaTagsSuccess({metaTags: response}))),
						catchError(error => of(metaTagsActions.loadMetaTagsFailure({error}))),
					);
				}
			}),
		),
	);

	/**
	 * Effect for retrieving the list of meta tag keys.
	 */
	reloadMetaTagKeys$ = createEffect(() =>
		this.actions$.pipe(
			ofType(metaTagsActions.reloadMetaTags),
			switchMap(({containDisables = false}) =>
				this.metaTagsService.getMetaTagKeys().pipe(
					map(metaTags => metaTags.filter(metaTag => containDisables || metaTag.isEnabled)),
					switchMap(response => of(metaTagsActions.loadMetaTagsSuccess({metaTags: response}))),
					catchError(error => of(metaTagsActions.loadMetaTagsFailure({error}))),
				),
			),
		),
	);

	getMetaTagKeysFailure$ = createEffect(
		() => {
			return this.actions$.pipe(
				ofType(metaTagsActions.loadMetaTagsFailure),
				tap(({error}) => {
					console.error('getMetaTagKeysFailure', JSON.stringify(error));
				}),
			);
		},
		{dispatch: false},
	);

	createMetaTag$ = createEffect(() =>
		this.actions$.pipe(
			ofType(metaTagsActions.addMetaTag),
			switchMap(action =>
				this.metaTagsService.createMetaTag(action.metaTag).pipe(
					switchMap(response => of(metaTagsActions.addMetaTagSuccess({newMetaTag: response}))),
					catchError(error => of(metaTagsActions.addMetaTagFailure({error}))),
				),
			),
		),
	);

	createMetaTagSuccess$ = createEffect(
		() => {
			return this.actions$.pipe(
				ofType(metaTagsActions.addMetaTagSuccess),
				tap(() => {
					this.toastService.open({
						message: 'struct.metatageditor.edit.success',
						type: SnackbarTypes.Info,
					});
				}),
			);
		},
		{dispatch: false},
	);

	createMetaTagFailure$ = createEffect(
		() => {
			return this.actions$.pipe(
				ofType(metaTagsActions.addMetaTagFailure),
				tap(() => {
					this.toastService.open({
						message: 'struct.metatageditor.edit.error',
						type: SnackbarTypes.Error,
					});
				}),
			);
		},
		{dispatch: false},
	);

	deleteMetaTag$ = createEffect(() =>
		this.actions$.pipe(
			ofType(metaTagsActions.deleteMetaTag),
			switchMap(action =>
				this.metaTagsService.deleteMetaTag(action.metaTagKey).pipe(
					switchMap(() => of(metaTagsActions.deleteMetaTagSuccess({metaTagKey: action.metaTagKey}))),
					catchError(error => of(metaTagsActions.deleteMetaTagFailure({error}))),
				),
			),
		),
	);

	deleteMetaTagSuccess$ = createEffect(
		() => {
			return this.actions$.pipe(
				ofType(metaTagsActions.deleteMetaTagSuccess),
				tap(() => {
					this.toastService.open({message: 'struct.metatageditor.delete.success', type: SnackbarTypes.Info});
				}),
			);
		},
		{dispatch: false},
	);

	deleteMetaTagFailure$ = createEffect(
		() => {
			return this.actions$.pipe(
				ofType(metaTagsActions.deleteMetaTagFailure),
				tap(() => {
					this.toastService.open({message: 'struct.metatageditor.delete.error', type: SnackbarTypes.Error});
				}),
			);
		},
		{dispatch: false},
	);

	updateMetaTag$ = createEffect(() =>
		this.actions$.pipe(
			ofType(metaTagsActions.updateMetaTag),
			switchMap(action =>
				this.metaTagsService.updateMetaTag(action.metaTag).pipe(
					switchMap(response => of(metaTagsActions.updateMetaTagSuccess({updatedMetaTag: response}))),
					catchError(error => of(metaTagsActions.updateMetaTagFailure({error}))),
				),
			),
		),
	);

	updateMetaTagSuccess$ = createEffect(
		() => {
			return this.actions$.pipe(
				ofType(metaTagsActions.updateMetaTagSuccess),
				tap(() => {
					this.toastService.open({message: 'struct.metatageditor.edit.success', type: SnackbarTypes.Info});
				}),
			);
		},
		{dispatch: false},
	);

	updateMetaTagFailure$ = createEffect(
		() => {
			return this.actions$.pipe(
				ofType(metaTagsActions.updateMetaTagFailure),
				tap(() => {
					this.toastService.open({message: 'struct.metatageditor.edit.error', type: SnackbarTypes.Error});
				}),
			);
		},
		{dispatch: false},
	);
}
